import { getRequestPath } from './authRequest';

export const TRANSIENT_NETWORK_RETRY_LIMIT = 2;
export const TRANSIENT_TIMEOUT_RETRY_LIMIT = 1;
export const CUSTOMER_CREATE_TIMEOUT_MS = 20000;

function canceledError(message, config, request) {
  const error = new Error(message || 'canceled');
  error.name = 'CanceledError';
  error.code = 'ERR_CANCELED';
  error.config = config;
  error.request = request;
  error.isAxiosError = true;
  return error;
}

function isSafeMethod(method) {
  const normalized = String(method || 'get').toLowerCase();
  return normalized === 'get' || normalized === 'head' || normalized === 'options';
}

export function isAbortedTransportError(error) {
  return error?.config?.signal?.aborted === true;
}

export function isTransientNetworkError(error) {
  if (!error?.isAxiosError || error.response || isAbortedTransportError(error)) {
    return false;
  }

  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
    return false;
  }

  return error.code === 'ERR_NETWORK' || error.message === 'Network Error';
}

export function shouldRetryTransientNetworkError(error) {
  if (!isTransientNetworkError(error)) {
    return false;
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }

  const config = error.config || {};
  if (config.networkRetry === false) {
    return false;
  }

  if (!isSafeMethod(config.method) && config.networkRetry !== true) {
    return false;
  }

  return (config.__networkRetryCount || 0) < TRANSIENT_NETWORK_RETRY_LIMIT;
}

export function transientNetworkRetryDelayMs(attempt) {
  return 300 * attempt;
}

export function isTransientTimeoutError(error) {
  if (!error?.isAxiosError || error.response || isAbortedTransportError(error)) {
    return false;
  }

  if (error.code !== 'ECONNABORTED' && error.code !== 'ETIMEDOUT') {
    return false;
  }

  return /timeout/i.test(error.message || '');
}

export function shouldRetryTransientTimeout(error) {
  if (!isTransientTimeoutError(error)) {
    return false;
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }

  const config = error.config || {};
  if (config.networkRetry === false) {
    return false;
  }

  // Reads can be repeated. A write is repeated only when the caller opted in
  // (login, logout, token validate). Customer create has its own recovery.
  if (!isSafeMethod(config.method) && config.networkRetry !== true) {
    return false;
  }

  if (isCustomerCreateRequest(config)) {
    return false;
  }

  return (config.__timeoutRetryCount || 0) < TRANSIENT_TIMEOUT_RETRY_LIMIT;
}

export function isCustomerCreateRequest(config = {}) {
  if (String(config.method || '').toLowerCase() !== 'post') {
    return false;
  }

  return getRequestPath(config).replace(/\/$/, '').endsWith('/customers');
}

function readRequestBody(data) {
  if (!data) {
    return null;
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  return typeof data === 'object' ? data : null;
}

function documentFromCustomerPayload(data) {
  const body = readRequestBody(data);
  const cuil = String(body?.cuil_cuit || '').replace(/\D/g, '');
  if (cuil.length < 5) {
    return '';
  }

  return cuil.slice(2, -1);
}

function matchingCreatedCustomer(results, documentNumber, cuil) {
  return (results || []).find((item) => {
    if (!item?.exists || item.id == null) {
      return false;
    }

    const dni = String(item.dni || '').replace(/\D/g, '');
    const itemCuil = String(item.cuit || item.cuil_cuit || '').replace(/\D/g, '');

    // Search is by DNI, so the list can include homonyms. Reuse an id only
    // when the row carries the CUIL that was posted.
    if (cuil !== '') {
      return itemCuil === cuil;
    }

    return dni === documentNumber;
  });
}

/**
 * A create that times out may already exist on the server. Look it up by
 * document and reuse that id only when the row CUIL is the one that was
 * posted. Repeat the POST when that person is not in the results.
 */
export async function settleStalledCustomerCreate(error, request) {
  const config = error.config || {};
  if ((config.__customerCreateRecovery || 0) >= 1) {
    return null;
  }

  const body = readRequestBody(config.data);
  const cuil = String(body?.cuil_cuit || '').replace(/\D/g, '');
  const documentNumber = documentFromCustomerPayload(config.data);
  if (!documentNumber) {
    return null;
  }

  let results = [];
  try {
    const search = await request({
      method: 'get',
      url: `/customers/search?document=${encodeURIComponent(documentNumber)}`,
      timeout: CUSTOMER_CREATE_TIMEOUT_MS,
      __customerCreateRecovery: 1,
    });
    results = search?.data?.results || [];
  } catch {
    return null;
  }

  const created = matchingCreatedCustomer(results, documentNumber, cuil);
  if (created) {
    return {
      data: {
        id: created.id,
        message: 'Cliente creado correctamente.',
      },
      status: 201,
      statusText: 'Created',
      config,
      headers: {},
    };
  }

  return request({
    ...config,
    timeout: CUSTOMER_CREATE_TIMEOUT_MS,
    __customerCreateRecovery: 1,
  });
}

/**
 * Safari reports an aborted XHR as "Network Error". A dropped mobile connection
 * does the same on requests that never received a response. Cancel the first
 * case, and retry the second when repeating the call cannot duplicate a write.
 */
export async function settleTransientNetworkError(error, request) {
  if (isAbortedTransportError(error)) {
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      throw error;
    }
    throw canceledError(error.message, error.config, error.request);
  }

  if (isCustomerCreateRequest(error.config) && isTransientTimeoutError(error)) {
    return settleStalledCustomerCreate(error, request);
  }

  const retryTimeout = shouldRetryTransientTimeout(error);
  if (!shouldRetryTransientNetworkError(error) && !retryTimeout) {
    return null;
  }

  const config = error.config;
  const attempt = retryTimeout
    ? (config.__timeoutRetryCount || 0) + 1
    : (config.__networkRetryCount || 0) + 1;
  if (retryTimeout) {
    config.__timeoutRetryCount = attempt;
  } else {
    config.__networkRetryCount = attempt;
  }

  const delayMs = typeof config.networkRetryDelayMs === 'number'
    ? config.networkRetryDelayMs
    : transientNetworkRetryDelayMs(attempt);

  if (delayMs > 0) {
    await new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }

  return request(config);
}
